import { spreadsheetsApi } from '../api/spreadsheets'

/**
 * F-140 (TASK-0394) — fogli di calcolo nel browser.
 *
 * - `readTabularFile`: gli import di lead, clienti ed esiti operatore leggono TESTO CSV. Un `.csv`/`.txt` si legge
 *   com'e'; un `.xlsx` passa da `POST /api/spreadsheets/to-csv` e torna come testo CSV: gli import non cambiano.
 * - Export dei report: la tabella gia' a video diventa CSV (nel browser, con la stessa protezione dalle formule dei
 *   file del server) o Excel (`POST /api/spreadsheets/xlsx`).
 */

export const EXCEL_ACCEPT = '.csv,.txt,.xlsx'
export const CSV_ACCEPT = '.csv,.txt'

export function isExcelFile(name: string): boolean {
  return /\.xlsx$/i.test(name.trim())
}

/** Il testo CSV di un file scelto dall'utente: `.csv`/`.txt` letti cosi' come sono, `.xlsx` convertito dal server. */
export async function readTabularFile(file: File): Promise<string> {
  if (/\.xls$/i.test(file.name)) {
    throw new Error('Il formato .xls non è supportato: salvare il file come .xlsx.')
  }
  if (isExcelFile(file.name)) return (await spreadsheetsApi.toCsv(file)).csv
  return file.text()
}

/* ── CSV nel browser ─────────────────────────────────────────────────────────────────────────── */

export type CellValue = string | number | boolean | null
export interface ReportTable {
  columns: string[]
  rows: CellValue[][]
  /** Falso quando `rows` contiene piu' blocchi di tabelle impilati. */
  autoFilter?: boolean
}

/** Un testo che un foglio leggerebbe come formula riceve un apice davanti (come `CsvTable.IsFormulaLike` sul server). */
export function neutraliseFormula(text: string): string {
  return text.length > 0 && '=+-@\t\r'.includes(text.charAt(0)) ? `'${text}` : text
}

function csvCell(value: CellValue): string {
  const text = neutraliseFormula(value === null ? '' : String(value))
  return /[;"\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text
}

/** CSV `;` RFC 4180, righe terminate da CRLF. */
export function buildCsv(table: ReportTable): string {
  const lines = [table.columns, ...table.rows].map(row => row.map(csvCell).join(';'))
  return lines.join('\r\n') + '\r\n'
}

/** Il file CSV con il BOM UTF-8 (Excel italiano altrimenti legge Windows-1252). */
export function csvBlob(table: ReportTable): Blob {
  return new Blob(['﻿', buildCsv(table)], { type: 'text/csv;charset=utf-8' })
}

export function fileSafeName(title: string): string {
  const safe = title.normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^A-Za-z0-9_-]+/g, '-').replace(/^-+|-+$/g, '')
  return (safe || 'report').slice(0, 60)
}

/* ── La tabella a video ──────────────────────────────────────────────────────────────────────── */

/**
 * Intestazioni che dicono "dati personali per riga" (lead, clienti, contatti, telefoni, e-mail, codici fiscali, IBAN,
 * soci): una tabella con una colonna cosi' (e valori che non sono solo numeri: "Lead" con i conteggi e' un dato
 * aggregato) NON si esporta dal browser. Servirebbe un export sul server, con approvazione e
 * audit (REQ-266-001).
 */
export const PERSONAL_HEADER = /^(telefono|cellulare|email|e-mail|codice fiscale|partita iva|iban|indirizzo|intestatario)\b/i
/** Intestazioni che possono anche essere un conteggio ("Lead", "Clienti": numeri): personali solo se i valori sono testo. */
export const PERSONAL_IF_TEXT_HEADER = /^(lead|cliente|clienti|contatto|contatti|socio|soci|titolare)\b/i

export interface ScreenTable extends ReportTable {
  title: string
}

export interface ScreenTables {
  tables: ScreenTable[]
  /** Tabelle a video lasciate fuori perche' hanno dati personali per riga. */
  skippedPersonal: number
}

function cellText(el: Element): string {
  return (el.textContent ?? '').replace(/\s+/g, ' ').trim()
}

/** Griglia di testo di un gruppo di righe, con `colSpan` e `rowSpan` espansi (le celle coperte restano vuote, salvo con `repeat`: intestazioni). */
function gridOf(rows: HTMLTableRowElement[], repeat = false): string[][] {
  const grid: string[][] = []
  rows.forEach((tr, r) => {
    const line: string[] = (grid[r] ??= [])
    let c = 0
    for (const cell of Array.from(tr.cells)) {
      while (line[c] !== undefined) c++
      const colSpan = Math.max(1, (cell as HTMLTableCellElement).colSpan || 1)
      const rowSpan = Math.max(1, (cell as HTMLTableCellElement).rowSpan || 1)
      const text = cellText(cell)
      for (let dc = 0; dc < colSpan; dc++) {
        for (let dr = 0; dr < rowSpan; dr++) {
          const row: string[] = (grid[r + dr] ??= [])
          row[c + dc] = repeat || (dr === 0 && dc === 0) ? text : ''
        }
      }
      c += colSpan
    }
  })
  return grid.map(row => Array.from(row, v => v ?? ''))
}

function titleOf(table: HTMLTableElement, index: number): string {
  const caption = table.caption ? cellText(table.caption) : ''
  if (caption) return caption
  const card = table.closest('.card')
  const heading = card?.querySelector('h1,h2,h3,h4,h5,h6,.card-title,.card-header')
  const text = heading ? cellText(heading) : ''
  return text || `Tabella ${index + 1}`
}

/** Le tabelle visibili dentro `root` (di solito il contenuto della pagina), senza quelle con dati personali per riga. */
export function tablesFromElement(root: ParentNode): ScreenTables {
  const tables: ScreenTable[] = []
  let skippedPersonal = 0
  const all = Array.from(root.querySelectorAll('table'))
  all.forEach((table, index) => {
    if (table.getClientRects().length === 0) return // nascosta
    const headRows = Array.from(table.tHead?.rows ?? [])
    const bodyRows = Array.from(table.tBodies).flatMap(b => Array.from(b.rows))
    if (headRows.length === 0 && bodyRows.length === 0) return

    const head = headRows.length > 0 ? gridOf(headRows, true) : gridOf(bodyRows.slice(0, 1), true)
    const body = headRows.length > 0 ? gridOf(bodyRows) : gridOf(bodyRows.slice(1))
    const width = Math.max(0, ...head.map(r => r.length), ...body.map(r => r.length))
    // Intestazione su piu' righe: le etichette di una colonna si uniscono ("Contratti - 2026-09").
    const labels = Array.from({ length: width }, (_, c) =>
      head.map(r => r[c] ?? '').filter((t, i, arr) => t !== '' && arr.indexOf(t) === i).join(' - '))
    // Una colonna "Lead" o "Clienti" di soli numeri e' un conteggio, non un elenco di persone.
    const isCount = (v: string) => /^[\d.,\s%€+-]*$/.test(v)
    if (labels.some((l, c) => PERSONAL_HEADER.test(l) || (PERSONAL_IF_TEXT_HEADER.test(l) && body.some(r => !isCount(r[c] ?? ''))))) {
      skippedPersonal += 1
      return
    }
    // Le colonne senza intestazione (azioni, selezione) non sono dati.
    const keep = labels.map((l, c) => (l !== '' ? c : -1)).filter(c => c >= 0)
    if (keep.length === 0) return
    tables.push({
      title: titleOf(table, index),
      columns: keep.map(c => labels[c]!),
      rows: body.filter(r => r.some(v => v !== '')).map(r => keep.map(c => r[c] ?? '')),
    })
  })
  return { tables, skippedPersonal }
}

/**
 * Una sola tabella da esportare. Con piu' tabelle a video le righe si impilano: riga vuota, titolo della tabella,
 * sua intestazione e i suoi dati; il filtro automatico dell'Excel resta spento.
 */
export function combineTables(tables: ScreenTable[]): ReportTable | null {
  const first = tables[0]
  if (!first) return null
  if (tables.length === 1) return { columns: first.columns, rows: first.rows, autoFilter: true }
  const width = Math.max(...tables.map(t => t.columns.length))
  const pad = (row: CellValue[]): CellValue[] => [...row, ...Array<CellValue>(width - row.length).fill('')]
  const rows: CellValue[][] = first.rows.map(pad)
  for (const t of tables.slice(1)) {
    rows.push(pad([]), pad([t.title]), pad(t.columns), ...t.rows.map(pad))
  }
  return { columns: pad(first.columns) as string[], rows, autoFilter: false }
}
